import { useMemo, useCallback, useEffect } from 'react'
import { Table, Col, Select, TreeSelect } from 'antd'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { getHtmlParentElement, renderTranslation } from '../../../Helpers/GenericHelpers'
import { SiteColumns } from '../../../Constants/TableConfiguration'
import { setCapitalLetter } from '../../../Helpers/StringHelpers'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { getEnabledSites, getSitePricelists, getSites } from 'Services/Settings/SiteSettingsService'
import { updateEnabled, updatePricelists } from 'Slices/Settings/SiteSlice'

import { getSiteId } from 'Helpers/NumericHelper'

function Sites(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const sites = useAppSelector(state => state.present.site.sites)
  const pricelists = useAppSelector(state => state.present.site.pricelists)
  const enabled = useAppSelector(state => state.present.site.enabled)
  const listings = useAppSelector(state => state.present.configuration.allPricelists)

  useEffect(() => {
    dispatch(getSites())
    dispatch(getSitePricelists())
    dispatch(getEnabledSites())
  }, [dispatch, listings.length])

  const listingData = useMemo(() => {
    return (
      listings.map(x => {
        return { value: x.name, label: x.name }
      }) ?? []
    )
  }, [listings])

  const onPricelistSelect = useCallback(
    (values, site) => {
      dispatch(
        updatePricelists({
          site: getSiteId(site),
          value: values.map(x => x.value),
        }),
      )
    },
    [dispatch],
  )

  const options = useMemo(() => {
    return [
      {
        value: true,
        label: renderTranslation(language, TranslationKeys.enablePricelistSetting),
      },
      {
        value: false,
        label: renderTranslation(language, TranslationKeys.disablePricelistSetting),
      },
    ]
  }, [language])

  const setEnabled = useCallback(
    (value: boolean, site: string) => {
      dispatch(
        updateEnabled({
          site: getSiteId(site),
          value: value,
        }),
      )
    },
    [dispatch],
  )

  const siteColumns = useMemo(() => {
    return Object.values(SiteColumns).map(x => {
      return {
        title: <div>{renderTranslation(language, TranslationKeys['siteTable' + setCapitalLetter(x)])}</div>,
        dataIndex: x,
        key: x,
        render: (text, record) => {
          switch (x) {
            case SiteColumns.Name:
              return text
            // case SiteColumns.Enabled:
            //   return (
            //     <Select
            //       options={options}
            //       value={text}
            //       onChange={(value: boolean) => {
            //         setEnabled(value, record[SiteColumns.Name])
            //       }}
            //       className='default-drop'
            //       getPopupContainer={getHtmlParentElement}
            //     />
            //   )
            case SiteColumns.Pricelists:
              return (
                <TreeSelect
                  treeData={listingData}
                  treeCheckable={true}
                  treeCheckStrictly={true}
                  onChange={value => {
                    onPricelistSelect(value, record[SiteColumns.Name])
                  }}
                  value={text.map(x => {
                    return { value: x, label: x }
                  })}
                  disabled={false}
                  getPopupContainer={getHtmlParentElement}
                />
              )
            default:
              return text
          }
        },
      }
    })
  }, [language, listingData, onPricelistSelect, options, setEnabled])

  const sitesData = useMemo(() => {
    const sitesEnabled = sites.filter(s => enabled.indexOf(getSiteId(s)) > -1)

    return sites && sites.length > 0
      ? sites.map(site => {
          return {
            [SiteColumns.Name]: site,
            [SiteColumns.Pricelists]: pricelists[getSiteId(site)] ?? [],
            // [SiteColumns.Enabled]: !!enabled.find(x => x === getSiteId(site)),
          }
        })
      : []
  }, [enabled, pricelists, sites])

  return (
    <Col span={24} className='site-setting-table-box mt-6'>
      <Table
        id='site-table'
        columns={siteColumns}
        rowKey={record => {
          return 'key-' + record.name
        }}
        dataSource={sitesData}
        pagination={false}
        tableLayout='fixed'
      />
    </Col>
  )
}

export default Sites
